import { useState } from 'react';
import { useTickets } from '@/hooks/useTickets';
import { useMyProducts } from '@/hooks/useProducts';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { LoadingSpinner } from '@/components/shared/LoadingStates';
import { Link } from 'react-router-dom';
import { Plus, Ticket, Package, ClipboardText, ArrowRight, Clock, CheckCircle, Wrench, HourglassSimple, Star, Phone } from '@phosphor-icons/react';
import { formatRelativeTime } from '@/lib/utils';
import useAppStore from '@/stores/appStore';

const CLOSED_STATUSES = ['closed', 'completed', 'rejected'];

// Ticket status to customer-friendly text
const getStatusInfo = (status) => {
  switch (status) {
    case 'submitted': return { text: 'Submitted', icon: HourglassSimple, color: 'text-slate-600', bg: 'bg-slate-100' };
    case 'picked_up': return { text: 'Under Review', icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50' };
    case 'approved': return { text: 'Approved', icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' };
    case 'assigned': return { text: 'Technician Assigned', icon: Wrench, color: 'text-violet-600', bg: 'bg-violet-50' };
    case 'in_progress': return { text: 'In Progress', icon: Wrench, color: 'text-amber-600', bg: 'bg-amber-50' };
    case 'completed': return { text: 'Completed', icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' };
    case 'closed': return { text: 'Closed', icon: CheckCircle, color: 'text-slate-500', bg: 'bg-slate-50' };
    default: return { text: status, icon: Clock, color: 'text-slate-600', bg: 'bg-slate-100' };
  }
};

export const CustomerDashboard = () => {
  const { data: tickets, isLoading: ticketsLoading } = useTickets();
  const { data: products, isLoading: productsLoading } = useMyProducts();
  const currentUser = useAppStore((state) => state.currentUser);
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const brandColor = tenantConfig?.brand_colour || '#002FA7';

  const allTickets = tickets?.data || [];
  const activeTickets = allTickets.filter(t => !CLOSED_STATUSES.includes(t.status));
  const closedTickets = allTickets.filter(t => CLOSED_STATUSES.includes(t.status));
  const recentTickets = allTickets.slice(0, 5);

  const firstName = currentUser?.full_name?.split(' ')[0] || 'there';

  return (
    <DashboardLayout role="customer">
      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-8 animate-fade-in">

        {/* Hero Welcome */}
        <div className="relative overflow-hidden rounded-2xl p-6 sm:p-8" style={{ background: `linear-gradient(135deg, ${brandColor}ee, ${brandColor}99)` }}>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-white/30" />
            <div className="absolute -left-6 -bottom-6 w-32 h-32 rounded-full bg-white/20" />
          </div>
          <div className="relative z-10">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white mb-1">
              Hi, {firstName} 👋
            </h1>
            <p className="text-white/80 text-sm sm:text-base mb-6">
              Track your service requests and manage your products
            </p>
            <Link to="/customer/tickets/new">
              <Button
                className="bg-white text-slate-900 font-bold hover:bg-white/90 shadow-lg px-6 h-11"
                data-testid="raise-ticket-hero-btn"
              >
                <Plus size={18} weight="bold" className="mr-2" />
                Raise a New Ticket
              </Button>
            </Link>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          <Link to="/customer/tickets?tab=active">
            <Card className="p-4 sm:p-5 border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100/50 hover:shadow-md hover:scale-[1.02] transition-all cursor-pointer" data-testid="active-tickets-card">
              <div className="flex flex-col items-center sm:items-start sm:flex-row sm:gap-3">
                <div className="p-2.5 bg-blue-500 rounded-xl mb-2 sm:mb-0 shadow-sm">
                  <Ticket size={20} className="text-white" weight="fill" />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-2xl sm:text-3xl font-black text-blue-900">{activeTickets.length}</p>
                  <p className="text-[11px] sm:text-xs font-semibold text-blue-600 uppercase tracking-wider">Active</p>
                </div>
              </div>
            </Card>
          </Link>

          <Link to="/customer/tickets?tab=past">
            <Card className="p-4 sm:p-5 border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-emerald-100/50 hover:shadow-md hover:scale-[1.02] transition-all cursor-pointer" data-testid="closed-tickets-card">
              <div className="flex flex-col items-center sm:items-start sm:flex-row sm:gap-3">
                <div className="p-2.5 bg-emerald-500 rounded-xl mb-2 sm:mb-0 shadow-sm">
                  <CheckCircle size={20} className="text-white" weight="fill" />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-2xl sm:text-3xl font-black text-emerald-900">{closedTickets.length}</p>
                  <p className="text-[11px] sm:text-xs font-semibold text-emerald-600 uppercase tracking-wider">Closed</p>
                </div>
              </div>
            </Card>
          </Link>

          <Link to="/customer/products">
            <Card className="p-4 sm:p-5 border-0 shadow-sm bg-gradient-to-br from-violet-50 to-violet-100/50 hover:shadow-md hover:scale-[1.02] transition-all cursor-pointer" data-testid="products-count-card">
              <div className="flex flex-col items-center sm:items-start sm:flex-row sm:gap-3">
                <div className="p-2.5 bg-violet-500 rounded-xl mb-2 sm:mb-0 shadow-sm">
                  <Package size={20} className="text-white" weight="fill" />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-2xl sm:text-3xl font-black text-violet-900">{products?.length || 0}</p>
                  <p className="text-[11px] sm:text-xs font-semibold text-violet-600 uppercase tracking-wider">Products</p>
                </div>
              </div>
            </Card>
          </Link>
        </div>

        {/* Active Tickets Section */}
        {activeTickets.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-xl font-black tracking-tight text-slate-900">Active Tickets</h2>
            </div>
            <div className="space-y-3">
              {activeTickets.map((ticket) => {
                const statusInfo = getStatusInfo(ticket.status);
                const StatusIcon = statusInfo.icon;
                return (
                  <Link key={ticket.id} to={`/customer/tickets/${ticket.id}`}>
                    <Card className="p-4 hover:shadow-md transition-all cursor-pointer border-l-4 group" style={{ borderLeftColor: brandColor }} data-testid={`ticket-card-${ticket.ticket_number}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="font-black text-sm text-slate-900">{ticket.ticket_number}</span>
                            <StatusBadge status={ticket.status} />
                          </div>
                          <p className="font-semibold text-sm text-slate-700 truncate">{ticket.product_name}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{ticket.issue_category}</p>
                          {ticket.tech_name && (
                            <div className="flex items-center gap-1.5 mt-2">
                              <Wrench size={12} className="text-slate-400" />
                              <span className="text-xs font-medium text-slate-600">{ticket.tech_name}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                          <span className="text-[11px] text-slate-400 font-medium">{formatRelativeTime(ticket.created_at)}</span>
                          <ArrowRight size={16} className="text-slate-300 group-hover:text-slate-600 transition-colors group-hover:translate-x-0.5 transform" />
                        </div>
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent Closed Tickets */}
        {closedTickets.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg sm:text-xl font-black tracking-tight text-slate-900">Past Tickets</h2>
              <Link to="/customer/tickets">
                <Button variant="ghost" size="sm" className="text-xs font-bold uppercase tracking-wider gap-1 text-slate-500 hover:text-slate-800" data-testid="view-all-tickets-btn">
                  View All <ArrowRight size={14} />
                </Button>
              </Link>
            </div>
            <div className="space-y-2">
              {closedTickets.slice(0, 3).map((ticket) => (
                <Link key={ticket.id} to={`/customer/tickets/${ticket.id}`}>
                  <Card className="p-3.5 hover:bg-slate-50 transition-all cursor-pointer border border-slate-100 group" data-testid={`ticket-card-${ticket.ticket_number}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="p-1.5 rounded-lg bg-slate-100 shrink-0">
                          <CheckCircle size={16} className="text-slate-400" weight="fill" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-sm text-slate-700">{ticket.ticket_number}</span>
                            <StatusBadge status={ticket.status} />
                          </div>
                          <p className="text-xs text-slate-500 truncate">{ticket.product_name} • {ticket.issue_category}</p>
                          {ticket.tech_name && (
                            <div className="flex items-center gap-2 mt-1.5">
                              <span className="text-[10px] text-slate-505 font-semibold">Technician: {ticket.tech_name}</span>
                              {ticket.is_rated ? (
                                <span className="inline-flex items-center gap-0.5 text-[9px] font-black text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                                  <Star size={10} weight="fill" /> Rated {ticket.rating_stars}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-0.5 text-[9px] font-black text-white bg-amber-500 hover:bg-amber-600 px-1.5 py-0.5 rounded shadow-sm animate-pulse">
                                  <Star size={10} weight="fill" /> Rate Technician
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <span className="text-[11px] text-slate-400 shrink-0">{formatRelativeTime(ticket.created_at)}</span>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!ticketsLoading && allTickets.length === 0 && (
          <Card className="p-12 sm:p-16 text-center border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
            <div className="p-4 bg-slate-100 rounded-2xl inline-flex mb-4">
              <ClipboardText size={40} className="text-slate-400" />
            </div>
            <h3 className="font-bold text-lg text-slate-700 mb-1">No tickets yet</h3>
            <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
              When you raise a service request, it will appear here. Get started by raising your first ticket.
            </p>
            <Link to="/customer/tickets/new">
              <Button className="font-bold px-6 h-11" style={{ backgroundColor: brandColor }} data-testid="raise-first-ticket-btn">
                <Plus size={18} className="mr-2" />
                Raise Your First Ticket
              </Button>
            </Link>
          </Card>
        )}

        {ticketsLoading && <LoadingSpinner />}
      </div>

      {/* Floating support call button */}
      {tenantConfig?.support_phone && (
        <a
          href={`tel:${tenantConfig.support_phone}`}
          className="fixed bottom-6 right-6 z-40 flex items-center justify-center w-14 h-14 rounded-full text-white shadow-2xl transition-all duration-300 hover:scale-110 active:scale-95 group focus:outline-none focus:ring-4 focus:ring-offset-2"
          style={{ 
            backgroundColor: brandColor,
            boxShadow: `0 8px 30px ${brandColor}66`,
            '--tw-ring-color': brandColor 
          }}
          title={`Call Support: ${tenantConfig.support_phone}`}
          data-testid="floating-support-call-btn"
        >
          <div className="absolute inset-0 rounded-full animate-ping opacity-25 group-hover:opacity-40" style={{ backgroundColor: brandColor }} />
          <Phone size={26} weight="fill" className="relative z-10 transition-transform group-hover:rotate-12" />
        </a>
      )}
    </DashboardLayout>
  );
};